<template>
    <div>
        <b-modal :title="getTitleModal" modal-class="modal-primary" hide-footer @hidden="$emit('close')"
            no-close-on-backdrop no-close-on-esc v-model="show" size="xmd">

            <div v-if="isReadOnly" class="mb-1">
                <b-badge variant="light-warning">
                    Bono en estado {{ modalData.estado }}, los ajustes ya no se pueden modificar.
                </b-badge>
            </div>

            <div class="d-flex w-full">
                <b-table-simple ref="table-tramo3-detalle" small responsive table-class="" sticky-header="60vh" hover>
                    <b-thead class="w-full">
                        <b-tr>
                            <b-th class="text-center">VIAJE</b-th>
                            <b-th class="text-center">ORDEN</b-th>
                            <b-th>CLIENTE</b-th>
                            <b-th>RUTA</b-th>
                            <b-th class="text-center">KM</b-th>
                            <b-th class="text-center">TIPO</b-th>
                            <b-th class="text-right">IMPORTE</b-th>
                            <b-th class="text-center">AJUSTE</b-th>
                            <b-th class="text-center">ACCIONES</b-th>
                        </b-tr>
                    </b-thead>

                    <b-tbody v-if="items.length">
                        <b-tr v-for="item in items" :key="item.id">
                            <b-td class="text-center font-weight-bold">{{ item.viaje || '-' }}</b-td>
                            <b-td class="text-center">{{ item.orden || '-' }}</b-td>
                            <b-td>{{ item.cliente || '-' }}</b-td>
                            <b-td>{{ item.ruta || '-' }}</b-td>
                            <b-td class="text-center">{{ item.km ? item.km + ' km' : '-' }}</b-td>
                            <b-td class="text-center">
                                <b-badge variant="light-secondary">{{ item.tipo_servicio || 'LOCAL' }}</b-badge>
                            </b-td>
                            <b-td class="text-right">
                                <span v-if="esAjustado(item)">
                                    <span class="text-muted"
                                        style="text-decoration: line-through; display: block; font-size: 11px;">
                                        S/ {{ item.monto_original | currency }}
                                    </span>
                                    <span class="text-primary font-weight-bold">
                                        S/ {{ item.monto_calculado | currency }}
                                    </span>
                                </span>
                                <span v-else class="text-primary">
                                    S/ {{ item.monto_original | currency }}
                                </span>
                            </b-td>
                            <b-td class="text-center">
                                <b-badge v-if="esAjustado(item)" variant="light-warning" v-b-tooltip.hover
                                    :title="item.motivo_ajuste || 'Ajustado'">
                                    AJUSTADO
                                </b-badge>
                                <span v-else class="text-muted">-</span>
                            </b-td>
                            <b-td class="text-center">
                                <div class="d-flex gap-x-2 justify-content-center" v-if="!isReadOnly">
                                    <feather-icon icon="Edit2Icon"
                                        class="text-primary cursor-pointer" v-b-tooltip.hover title="Ajustar importe"
                                        @click="abrirAjuste(item)" />
                                    <feather-icon v-if="esAjustado(item)" icon="RotateCcwIcon"
                                        class="text-danger cursor-pointer" v-b-tooltip.hover title="Revertir a importe original"
                                        @click="revertirAjuste(item)" />
                                </div>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                    <b-tbody v-else>
                        <b-tr>
                            <b-td colspan="9" class="text-center py-2">
                                <span class="text-muted">No se encontraron viajes en el tramo (250-980km) para este bono.</span>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                    <b-tfoot v-if="items.length">
                        <b-tr>
                            <b-td colspan="6" class="text-right font-weight-bold" style="opacity: 0.8; font-size: 13px">
                                TOTAL TRAMO 3 (250-980KM):
                            </b-td>
                            <b-td colspan="3" style="font-size: 13px">
                                <b-badge variant="light-primary" class="font-weight-bold">
                                    S/ {{ totalTramo3 | currency }}
                                </b-badge>
                            </b-td>
                        </b-tr>
                    </b-tfoot>
                </b-table-simple>
            </div>

            <div class="d-flex justify-content-end gap-x-3 mt-1">
                <b-button variant="outline-primary" @click="$emit('close')">
                    CERRAR
                </b-button>
            </div>
        </b-modal>

        <AjusteTramo3Modal v-if="showAjusteModal" :modalData="ajusteData" @close="showAjusteModal = false"
            @refresh="handleAjusteGuardado" />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import BonoService from "@/views/rrhh/views/payroll/components/bonos/service/bono.service.js";
import AjusteTramo3Modal from "@/views/rrhh/views/payroll/components/bonos/components/AjusteTramo3Modal.vue";

const ESTADOS_NO_EDITABLES = ["APROBADO", "ANULADO"];

export default {
    components: {
        AjusteTramo3Modal,
    },
    props: {
        modalData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            show: true,
            items: [],
            showAjusteModal: false,
            ajusteData: null,
        };
    },
    async mounted() {
        await this.getTramo3Detalle();
    },
    computed: {
        ...mapGetters({
            currentUser: "auth/currentUser",
        }),
        getTitleModal() {
            return "DETALLE VIAJES (250-980KM): " + (this.modalData && this.modalData.nombre_completo ? this.modalData.nombre_completo : "");
        },
        isReadOnly() {
            return ESTADOS_NO_EDITABLES.includes(this.modalData.estado);
        },
        totalTramo3() {
            return this.items.reduce((acc, item) => acc + Number(item.monto_calculado || 0), 0);
        },
    },
    methods: {
        esAjustado(item) {
            return item.monto_ajustado !== null && item.monto_ajustado !== undefined;
        },
        async getTramo3Detalle() {
            try {
                const { data } = await BonoService.getTramo3Detalle({ bono_id: this.modalData.id });
                this.items = data.data || [];
            } catch (error) {
                this.items = [];
                console.log(error);
            }
        },
        abrirAjuste(item) {
            this.ajusteData = item;
            this.showAjusteModal = true;
        },
        async revertirAjuste(item) {
            try {
                const confirm = await this.showConfirmSwal();
                if (!confirm.isConfirmed) return;

                this.addPreloader();
                await BonoService.quitarAjusteTramo3({ id: item.id });
                this.showToastSuccess("Ajuste revertido correctamente");
                await this.getTramo3Detalle();
                this.$emit("refresh");
            } catch (error) {
                this.showToastError(error?.response?.data?.message);
            } finally {
                this.removePreloader();
            }
        },
        async handleAjusteGuardado() {
            this.showAjusteModal = false;
            await this.getTramo3Detalle();
            this.$emit("refresh");
        },
    },
};
</script>
